import { 
  LayoutGrid, 
  MessageSquare, 
  Calendar, 
  Bell, 
  Users, 
  CreditCard, 
  FileText,
  HelpCircle,
  Settings
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  { icon: LayoutGrid, label: "Orders" },
  { icon: MessageSquare, label: "Messages", active: true },
  { icon: Calendar, label: "Calendar" },
  { icon: Bell, label: "Notifications", badge: 3 },
  { icon: Users, label: "Community" },
  { icon: CreditCard, label: "Pricing Plan" },
  { icon: FileText, label: "Billing & Invoices" },
];

const bottomNavItems: NavItem[] = [
  { icon: Settings, label: "Settings" },
  { icon: HelpCircle, label: "Help & Support" },
];

interface SidebarProps {
  className?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ className, collapsed = false }: SidebarProps) {
  return (
    <aside 
      className={cn(
        "flex flex-col h-full bg-card border-r border-border transition-all duration-300 ease-out",
        collapsed ? "w-[72px]" : "w-[240px]",
        className
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <span className="text-primary-foreground font-bold text-sm">P</span>
        </div>
        {!collapsed && (
          <span className="font-semibold text-foreground animate-fade-in">Passionbits</span>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {mainNavItems.map((item) => (
          <NavItemButton key={item.label} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Divider */}
      <div className="px-4">
        <div className="h-px bg-border" />
      </div>

      {/* Bottom Navigation */}
      <nav className="px-3 py-4 space-y-1">
        {bottomNavItems.map((item) => (
          <NavItemButton key={item.label} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* User Profile */}
      <div className="px-3 py-4 border-t border-border">
        <div className={cn(
          "flex items-center gap-3 p-2 rounded-xl hover:bg-muted transition-colors duration-200 cursor-pointer",
          collapsed && "justify-center"
        )}>
          <Avatar className="w-9 h-9 flex-shrink-0">
            <AvatarImage src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">KA</AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0 animate-fade-in">
              <p className="text-sm font-medium text-foreground truncate">Karthik Aryan</p>
              <p className="text-xs text-muted-foreground truncate">Admin</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function NavItemButton({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  
  return (
    <button
      className={cn(
        "nav-item w-full",
        item.active && "nav-item-active",
        collapsed && "justify-center px-0"
      )}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left text-sm animate-fade-in">{item.label}</span>
          {item.badge && (
            <span className="unread-badge animate-scale-in">{item.badge}</span>
          )}
        </>
      )}
    </button>
  );
}
